<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import apolloClient from '@/apollo/client';
import { useMutation } from '@vue/apollo-composable';
import { v4 as uuidv4 } from 'uuid';
import {
  ComputeTaskCreateDocument,
  CouponApplicationInput,
  FileUploadInput,
  UploadsCreateDocument,
} from '@/gql/graphql';
import { ApolloCacheUtils } from '@/utils/cache-utils';
import {
  BAlert,
  BCard,
  BCollapse,
  BButton,
  BInputGroup,
  BForm,
  BFormCheckbox,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BFormTextarea,
  BSpinner,
  BTooltip,
} from 'bootstrap-vue-next';
import { formatFileSize, formatFileName } from '@/utils/file-utils';
import { buildStorageTargetOptions } from '@/utils/storage-provider-options';
import { buildWasabiTransferInput } from '@/utils/wasabi-transfer-input';
import {
  Attributes_FtpTransfer,
  Attributes_BaseSpaceTransfer,
  Attributes_Common,
  Attributes_WasabiTransfer,
} from '@/utils/compute-utils';
import CodeValidation from '@/components/CodeValidation.vue';
import {
  COUPON_TYPE_NAMES,
  COUPON_UNITS,
  IS_DEBUG_MODE,
  COMPUTE_TASK_TYPES,
  UPLOAD_MODES,
  UploadModeType,
} from '@/constants';
import { useUploadsStore } from '@/store/uploads';
import { useUserStore } from '@/store/user';
import { useBaseSpaceStore } from '@/store/basespace';
import CloudRegionSelection from '@/components/compute/CloudRegionSelection.vue';
import DeveloperOptions from '@/components/compute/DeveloperOptions.vue';
import PipelinePicker from '@/components/compute/PipelinePicker.vue';
import { PipelineType } from '@/gql/graphql';

// use uploads store to start new uploads
const uploadsStore = useUploadsStore();
const userStore = useUserStore();
const bsStore = useBaseSpaceStore();

// --- Component references ---
const devOptions = ref<InstanceType<typeof DeveloperOptions>>();

// --- Component properties ---
const props = defineProps<{
  selectedUploadOption: UploadModeType;
}>();

// --- Component emits ---
const emits = defineEmits(['started-data-transfer', 'started-web-uploads']);

// --- Local-Only State ---
const files = ref<File[]>([]);
const isDragOver = ref(false);
const fileInput = ref<HTMLInputElement | null>(null);

// --- NextFlow Pipeline specific state ---
const selectedPipeline = ref<PipelineType | null>(null);
const nfCustomWorkflow = ref<boolean>(false);
const nfWorkflowLocation = ref<string>('');

// --- Upload Data ---
const codeValidation = ref<InstanceType<typeof CodeValidation>>();
const newBucketName = ref('');
const newBucketDescription = ref('');
const NewBucketRegionPrefix = ref<string | null>(null);
const customCloudRegionRequired = ref(false);
const selectedStorageTarget = ref('gcp:');
const storageTargetOptions = computed(() => {
  const environments =
    userStore.activeOrgAssociation?.organization?.cloudEnvironments || [];
  return buildStorageTargetOptions(environments);
});
const selectedStorageProvider = computed(
  () => selectedStorageTarget.value.split(':', 1)[0]
);
const selectedCloudEnvironmentId = computed(() => {
  const rawId = selectedStorageTarget.value.split(':')[1];
  return rawId ? Number(rawId) : null;
});
const uploadWeb = computed(
  () => props.selectedUploadOption === UPLOAD_MODES.WEB
);
const uploadFtp = computed(
  () => props.selectedUploadOption === UPLOAD_MODES.FTP
);
const uploadWasabi = computed(
  () => props.selectedUploadOption === UPLOAD_MODES.WASABI
);
const uploadBasespace = computed(
  () => props.selectedUploadOption === UPLOAD_MODES.BASESPACE
);
const componentTitle = computed(() => {
  if (uploadWeb.value) {
    return 'Upload Data from Your Computer';
  } else if (uploadFtp.value) {
    return 'Transfer Data from an FTP Server';
  } else if (uploadWasabi.value) {
    return 'Transfer Data from Wasabi S3';
  } else if (uploadBasespace.value) {
    return 'Transfer Data from BaseSpace';
  }
  // otherwise
  return 'Upload New Data';
});

// --- FTP Transfer Command Examples ---
// 2024-12 "lftp -c 'set ssl:verify-certificate no; set ftp:ssl-force true; set ftp:ssl-protect-data true; open -u user,password ftp://34.71.95.61:2121; mirror -c 225-C1x-280; quit'"
// 2025-05 "lftp -c 'set sftp:auto-confirm yes;set net:max-retries 20;open sftp://X202SC25044156-Z01-F001:xs2asdr6@usftp23.novogene.com; mirror --verbose --use-pget-n=8 -c'"
// 2025-09 "lftp -c 'set ssl:verify-certificate no; set ftp:ssl-force true; set ftp:ssl-protect-data true; open -u user,password ftp://34.71.95.61:2121; mirror -c 225-C1x-280; quit'"
// 2025-09 "lftp -c 'set sftp:auto-confirm yes; set net:max-retries 20; open sftp://ftp:password@34.71.95.61:22; mirror --verbose --use-pget-n=8 -c data/'"
const linesFtp = ref(
  IS_DEBUG_MODE || userStore.developer
    ? "lftp -c 'set ssl:verify-certificate no; set ftp:ssl-force true; set ftp:ssl-protect-data true; open -u user,password ftp://34.71.95.61:2121; mirror -c s1-4-v2; quit'"
    : ''
);

const wasabiSourceUri = ref('');
const wasabiEndpointUrl = ref('');
const wasabiAccessKeyId = ref('');
const wasabiSecretAccessKey = ref('');

const uploadWebBcl = computed(() => {
  return files.value.some((file) => {
    const fileName = file.name.toLowerCase();
    return fileName.endsWith('.bcl') || fileName.endsWith('.bcl.tar');
  });
});

// require even number of fastq files for web upload
const uploadWebOddFastqFilesCount = computed(() => {
  if (!uploadWeb.value) {
    return false;
  }
  // count number of files with fastq or fastq.gz extension
  const fastqFiles = files.value.filter((file) => {
    const fileName = file.name.toLowerCase();
    return fileName.endsWith('.fastq') || fileName.endsWith('.fastq.gz');
  });
  // check if the number of fastq files is odd
  return fastqFiles.length % 2 !== 0;
});
const autoFeaturesDisabled = computed(() => {
  // disabled on multiple kit/coupon codes
  if (
    codeValidation.value &&
    codeValidation.value.codeValidationItems &&
    codeValidation.value.codeValidationItems.length > 1
  ) {
    return true;
  }

  // disabled on WEB upload
  if (uploadWeb.value) {
    return true;
  }

  // otherwise, auto-features are enabled
  return false;
});
const autoSampleAnnotation = ref(false);
const autoProjectDefinition = ref(false);
const autoSampleReconstruction = ref(false);

watch(autoSampleAnnotation, (newAutoSampleAnnotation) => {
  if (!newAutoSampleAnnotation) {
    autoProjectDefinition.value = false;
  }
});

watch(autoProjectDefinition, (newAutoProjectDefinition) => {
  if (newAutoProjectDefinition) {
    autoSampleAnnotation.value = true;
  } else {
    autoSampleReconstruction.value = false;
  }
});

watch(autoSampleReconstruction, (newAutoSampleReconstruction) => {
  if (newAutoSampleReconstruction) {
    autoProjectDefinition.value = true;
  }
});

// --- Storage Coupon Units ---
const minRequiredStorageCouponUnits = computed(() => {
  // WEB upload: require 1 sample for each 2 uploaded fastq files
  if (uploadWeb.value) {
    // return number of files with fastq or fastq.gz extension divided by 2
    const fastqFiles = files.value.filter((file) => {
      const fileName = file.name.toLowerCase();
      return fileName.endsWith('.fastq') || fileName.endsWith('.fastq.gz');
    });
    // return fastqFiles.length / 2, but minimum 1
    let minUnits = Math.ceil(fastqFiles.length / 2);
    if (minUnits < 1) {
      minUnits = 1;
    }
    return minUnits;
  }
  // FTP transfer: require at least 1 sample per line
  else if (uploadFtp.value) {
    return linesFtp.value.split('\n').filter((line) => line).length;
  }
  // WASABI transfer: require at least 1 sample
  else if (uploadWasabi.value) {
    return 1; // require at least 1 sample for Wasabi transfer
  }
  // BaseSpace upload: require at least 1 sample
  return bsStore.bsSelection.samples.length || 1;
});

const declaredStorageCouponUnits = computed(() => {
  return codeValidation.value?.codeValidationItems.reduce(
    (acc, item) => acc + item.unitsDeclared,
    0
  );
});

watch(
  () => props.selectedUploadOption,
  (newValue) => {
    if (newValue === UPLOAD_MODES.WEB) {
      autoSampleAnnotation.value = false;
      autoProjectDefinition.value = false;
      autoSampleReconstruction.value = false;
    }
  }
);

watch(
  () => codeValidation.value?.codeValidationItems.length,
  (newLength) => {
    if (newLength !== undefined) {
      if (newLength > 1) {
        autoSampleAnnotation.value = false;
        autoProjectDefinition.value = false;
        autoSampleReconstruction.value = false;
      }
    }
  }
);

// --- Mutations State ---
const mutationInProgress = ref<boolean>(false);
const mutationError = ref<string | null>(null);
const mutationShowSuccess = ref<boolean>(false);

// --- Mutation: Create Compute Task (BaseSpace/FTP/Wasabi Transfer) ---
const {
  mutate: executeComputeTaskCreateMutation,
  onDone: onComputeTaskCreateMutationDone,
  onError: onComputeTaskCreateMutationError,
} = useMutation(ComputeTaskCreateDocument);

onComputeTaskCreateMutationDone((res) => {
  mutationInProgress.value = false;
  if (res.data && res.data.computeTaskCreate) {
    // BaseSpace of FTP data transfer started successfully
    console.debug('Data transfer task created:', res.data.computeTaskCreate.id);
    emits('started-data-transfer');

    // Add the new bucket to the current user's cached bucketsAssociated list.
    if (res.data.computeTaskCreate.bucket && userStore.id) {
      ApolloCacheUtils.addUserBucket(
        apolloClient.cache,
        userStore.id,
        res.data.computeTaskCreate.bucket
      );
    }

    // report success
    mutationShowSuccess.value = true;
    setTimeout(() => {
      mutationShowSuccess.value = false;
    }, 1000);
  } else {
    mutationError.value = 'FTP transfer init failed: ' + res.data;
    console.error('FTP transfer initialization failed.');
  }
});

onComputeTaskCreateMutationError((err) => {
  console.error('Error starting FTP transfer:', err.message);
  mutationInProgress.value = false;
  mutationError.value = err.message;
});

// --- Mutation: Create Uploads (WEB Upload) ---
const {
  mutate: executeWebUploadsStartMutation,
  onDone: onWebUploadsStartMutationDone,
  onError: onWebUploadsStartMutationError,
} = useMutation(UploadsCreateDocument);

onWebUploadsStartMutationDone((res) => {
  mutationInProgress.value = false;
  if (res.data && res.data.uploadsCreate) {
    const createdUploadCount = res.data.uploadsCreate.length;
    console.debug('WEB upload records created:', createdUploadCount);

    // Add the new bucket to the current user's cached bucketsAssociated list.
    if (res.data.uploadsCreate.length > 0 && userStore.id) {
      if (res.data.uploadsCreate[0]?.bucket) {
        ApolloCacheUtils.addUserBucket(
          apolloClient.cache,
          userStore.id,
          res.data.uploadsCreate[0].bucket
        );
      }
    }

    // start uploading
    for (let i = 0; i < res.data.uploadsCreate.length; i++) {
      const upload = res.data.uploadsCreate[i];
      if (!upload?.id) {
        console.error('Upload record is missing its ID at index:', i);
        continue;
      }
      // find corresponding item in files
      const file = files.value.find(
        (f) => f.name === formatFileName(upload.sourceFileName)
      );
      if (!file) {
        console.error(
          'File not found for:',
          formatFileName(upload.sourceFileName)
        );
        continue;
      }
      // start the upload
      console.debug('Uploading file:', file.name, file.size);
      void uploadsStore.resumeUpload(upload, file).catch((error) => {
        mutationError.value =
          error instanceof Error
            ? error.message
            : 'Upload could not be started.';
      });
    }

    // report success
    emits('started-web-uploads');
    mutationShowSuccess.value = true;
    setTimeout(() => {
      mutationShowSuccess.value = false;
    }, 1000);
  } else {
    mutationError.value = 'WEB uploads init failed: ' + res.data;
    console.error('WEB upload initialization failed.');
  }
});

onWebUploadsStartMutationError((err) => {
  console.error('Error starting WEB uploads:', err.message);
  mutationInProgress.value = false;
  mutationError.value = err.message;
});

// --- Handlers for the Drag & Drop and File Input ---
function onDragOver(event: DragEvent) {
  event.preventDefault();
  isDragOver.value = true;
}

function onDragLeave() {
  isDragOver.value = false;
}

function onDrop(event: DragEvent) {
  isDragOver.value = false;
  if (event.dataTransfer) {
    const droppedFiles = event.dataTransfer.files;
    addFiles(droppedFiles);
  }
}

function handleFileSelect(event: Event) {
  const input = event.target as HTMLInputElement;
  if (input.files) {
    addFiles(input.files);
  }
}

function addFiles(newFiles: FileList) {
  for (let i = 0; i < newFiles.length; i++) {
    const file = newFiles.item(i);
    if (file) {
      files.value.push(file);
    }
  }
}

function browseFiles() {
  fileInput.value?.click();
}

function onStartUpload() {
  if (uploadWeb.value) {
    startWebUpload();
  } else {
    startDataTransfer();
  }
}

function startDataTransfer() {
  console.debug('Data transfer requested:', props.selectedUploadOption);
  mutationInProgress.value = true;
  mutationError.value = null;

  if (devOptions.value?.extraAttributesInvalid) {
    mutationError.value =
      'Developer JSON attributes must be a valid JSON object.';
    mutationInProgress.value = false;
    return;
  }

  const couponApplications: CouponApplicationInput[] =
    codeValidation?.value?.codeValidationItems.map((item) => ({
      code: item.codeString,
      unitsDeclared: item.unitsDeclared,
    })) || [];

  // define attributes: 1. common attributes
  const attributes:
    | Attributes_Common
    | Attributes_BaseSpaceTransfer
    | Attributes_FtpTransfer
    | Attributes_WasabiTransfer = {
    gceRequestId: uuidv4(),
    newBucketFriendlyName: newBucketName.value,
    newBucketDescription: newBucketDescription.value,
    createProject: autoProjectDefinition.value,
    reconstructData: autoSampleReconstruction.value,
  };

  // FTP/BaseSpace/Wasabi task-specific: attributes, type, name and description
  let taskType: string;
  let taskName: string;
  let taskDescription: string;

  if (props.selectedUploadOption === UPLOAD_MODES.FTP) {
    attributes.ftpCmd = linesFtp.value;
    attributes.autoAnnotate = autoSampleAnnotation.value;
    taskType = COMPUTE_TASK_TYPES.FTP_TRANSFER;
    taskName = 'FTP Transfer for ' + newBucketName.value;
    taskDescription = 'FTP transfer of ' + newBucketName.value + ' data';
  } else if (props.selectedUploadOption === UPLOAD_MODES.BASESPACE) {
    attributes.bsFilesList = bsStore.bsTransferItems;
    taskType = COMPUTE_TASK_TYPES.BASESPACE_TRANSFER;
    taskName = 'BaseSpace Data Transfer for ' + newBucketName.value;
    taskDescription =
      'Transfer of ' +
      newBucketName.value +
      " data from Illumina's BaseSpace Sequence Hub";
  } else if (props.selectedUploadOption === UPLOAD_MODES.WASABI) {
    attributes.wasabiTransfer = buildWasabiTransferInput({
      sourceUri: wasabiSourceUri.value,
      endpointUrl: wasabiEndpointUrl.value,
      accessKeyId: wasabiAccessKeyId.value,
      secretAccessKey: wasabiSecretAccessKey.value,
    });
    attributes.autoAnnotate = autoSampleAnnotation.value;
    taskType = COMPUTE_TASK_TYPES.WASABI_TRANSFER;
    taskName = 'Wasabi Data Transfer for ' + newBucketName.value;
    taskDescription =
      'Transfer of ' + newBucketName.value + ' data from Wasabi S3 storage';
  } else {
    console.error(
      'Unknown upload mode for data transfer:',
      props.selectedUploadOption
    );
    return;
  }

  // pipeline selection for auto-sample reconstruction
  if (attributes.reconstructData) {
    if (nfCustomWorkflow.value) {
      attributes.workflowLocation = nfWorkflowLocation.value;
    } else if (selectedPipeline.value && selectedPipeline.value.id) {
      attributes.pipelineId = selectedPipeline.value.id;
    }
  }

  // add developer options
  if (userStore.developer && devOptions.value?.expanded) {
    // general - version
    if (devOptions.value.version) {
      attributes.version = devOptions.value.version;
    }

    // general - vm lifecycle
    if (devOptions.value.vmLifecycle) {
      attributes.vmLifecycle = devOptions.value.vmLifecycle;
    }

    // general - machine type
    if (devOptions.value.machineType) {
      attributes.machineType = devOptions.value.machineType;
    }

    // extra attributes from JSON
    if (devOptions.value.extraAttributes) {
      Object.assign(attributes, devOptions.value.extraAttributes);
    }
  }

  // execute mutation
  console.debug('Starting data transfer task:', taskType);

  // mutationInProgress.value = false;
  executeComputeTaskCreateMutation({
    coupons: couponApplications,
    name: taskName,
    description: taskDescription,
    taskType: taskType,
    attributes: attributes,
    requestedRegionPrefix: NewBucketRegionPrefix.value
      ? NewBucketRegionPrefix.value
      : undefined,
  });
}

function startWebUpload() {
  console.debug(
    'Starting WEB uploads:',
    files.value.length,
    selectedStorageProvider.value
  );
  mutationInProgress.value = true;
  mutationError.value = null;

  const couponApplications: CouponApplicationInput[] =
    codeValidation?.value?.codeValidationItems.map((item) => ({
      code: item.codeString,
      unitsDeclared: item.unitsDeclared,
    })) || [];

  const filesInput: FileUploadInput[] = files.value.map((file) => ({
    sourceFileName: file.name,
    targetFileName: file.name.split('/').pop() || file.name,
    fileSize: file.size.toString(),
  }));

  // execute mutation
  executeWebUploadsStartMutation({
    coupons: couponApplications,
    name: newBucketName.value,
    description: newBucketDescription.value,
    files: filesInput,
    bucketName: null,
    bucketPath: null,
    requestedRegionPrefix: NewBucketRegionPrefix.value
      ? NewBucketRegionPrefix.value
      : null,
    cloudProvider: selectedStorageProvider.value,
    cloudEnvironmentId: selectedCloudEnvironmentId.value,
  });
}
</script>

<template>
  <div>
    <b-card :title="componentTitle">
      <!-- Upload/Transfer Brief Description-->
      <p v-if="uploadWeb">
        Upload new data to the analytics platform directly from your computer
        via web interface. If upload process gets interrupted, you can resume it
        later without losing your progress.
      </p>
      <p v-else-if="uploadFtp">
        Transfer data from an FTP server of your sequencing provider to the
        analytics platform. Once the process is started, you can leave the
        platform and come back later to check the status. If you have your
        notifications enabled, you will receive an e-mail when the transfer is
        complete.
      </p>
      <p v-else-if="uploadWasabi">
        Transfer data from a Wasabi S3 hot cloud storage to the analytics
        platform. Once the process is started, you can leave the platform and
        come back later to check the status. If you have your notifications
        enabled, you will receive an e-mail when the transfer is complete.
      </p>
      <p v-else-if="uploadBasespace">
        Transfer data from Illumina BaseSpace Sequence Hub to the analytics
        platform. Once the process is started, you can leave the platform and
        come back later to check the status. If you have your notifications
        enabled, you will receive an e-mail when the transfer is complete.
      </p>
      <!-- <UploadComponent /> -->
      <div>
        <BForm @submit.prevent="onStartUpload">
          <!-- Data Identification - Name and Description -->
          <h5 class="mt-4">Label your data</h5>
          <b-form-group class="mt-3">
            <label for="bucket-name">
              <font-awesome-icon :icon="['fas', 'tag']" class="me-1" />
              Data label
              <font-awesome-icon
                :icon="['fas', 'question-circle']"
                class="ms-2 text-secondary"
                id="bucket-name-help-icon"
              />
              <b-tooltip target="bucket-name-help-icon"
                >A label to help you identify your data later.
              </b-tooltip>
            </label>
            <b-form-input
              id="bucket-name"
              name="bucketName"
              v-model="newBucketName"
              type="text"
              required
            />
          </b-form-group>

          <b-form-group class="mt-3">
            <label for="bucket-description">
              <font-awesome-icon
                :icon="['fas', 'align-justify']"
                class="me-1"
              />
              Data description
              <font-awesome-icon
                :icon="['fas', 'question-circle']"
                class="ms-2 text-secondary"
                id="bucket-description-help-icon"
              />
              <b-tooltip target="bucket-description-help-icon">
                Optional description of the data to help you identify your data.
              </b-tooltip>
            </label>
            <b-form-textarea
              id="bucket-description"
              name="bucketDescription"
              v-model="newBucketDescription"
              type="text"
              rows="2"
              max-rows="10"
            ></b-form-textarea>
          </b-form-group>

          <!-- Cloud Region (Data Residency) -->
          <CloudRegionSelection
            v-model="NewBucketRegionPrefix"
            v-model:modelRequired="customCloudRegionRequired"
            class="mt-3 mb-3"
          />

          <b-form-group v-if="uploadWeb" class="mt-3 mb-3">
            <label for="storage-provider">
              <font-awesome-icon :icon="['fas', 'cloud']" class="me-1" />
              Storage provider
            </label>
            <b-form-select
              id="storage-provider"
              v-model="selectedStorageTarget"
              :options="storageTargetOptions"
              required
            />
          </b-form-group>

          <!-- FTP(S) -->
          <div v-if="uploadFtp">
            <b-form-group id="lines-ftp" class="mb-2">
              <label for="lines-ftp">
                <font-awesome-icon :icon="['fas', 'code']" class="me-1" />
                Paste complete commands provided by your sequencing provider
                <font-awesome-icon
                  :icon="['fas', 'question-circle']"
                  class="ms-2 text-secondary"
                  id="lines-ftp-help-icon"
                />
                <b-tooltip target="lines-ftp-help-icon" class="large-tooltip">
                  <div class="text-start">
                    <p>
                      You can provide multiple commands, one per line, to
                      transfer data from multiple sources. Make sure to include
                      all necessary authentication details (username, password,
                      keys, etc.) in the commands. Double-check the commands
                      before starting the transfer. Incorrect commands may lead
                      to transfer failures or data loss.
                    </p>
                    <p class="mb-1">
                      <strong>Here are some examples of valid commands:</strong>
                    </p>
                    <ul style="list-style-type: disc; padding-left: 1rem">
                      <li class="mb-2">
                        <code>
                          wget -r -b -c --user=X012SC34567890-Z01-F002
                          --password=secret ftp://ftp123.genedatabase.com:21/
                        </code>
                      </li>
                      <li>
                        <code>
                          lftp -c 'set sftp:auto-confirm yes;set net:max-retries
                          20;open
                          sftp://X123SC0123456-Z01-F001:secret@ftp123.genedatabase.com;
                          mirror --verbose --use-pget-n=8 -c'
                        </code>
                      </li>
                    </ul>
                  </div>
                </b-tooltip>
              </label>
              <b-form-textarea
                name="lines-ftp"
                v-model="linesFtp"
                rows="4"
                max-rows="12"
                required
              ></b-form-textarea>
            </b-form-group>
          </div>

          <!-- WASABI -->
          <div v-if="uploadWasabi">
            <h5 class="mt-4">Wasabi Storage Details & Credentials</h5>
            <!-- W Endpoint URL / Server -->
            <b-form-group class="mt-3">
              <label for="w-endpoint-url">
                <font-awesome-icon :icon="['fas', 'server']" class="me-1" />
                Server
                <font-awesome-icon
                  :icon="['fas', 'question-circle']"
                  class="ms-2 text-secondary"
                  id="w-endpoint-url-help-icon"
                />
              </label>
              <b-tooltip target="w-endpoint-url-help-icon">
                Endpoint URL
              </b-tooltip>
              <b-form-input
                id="w-endpoint-url"
                name="wEndpointUrl"
                v-model="wasabiEndpointUrl"
                type="text"
                required
              />
            </b-form-group>
            <!-- W Source Uri / AWS CLI Path -->
            <b-form-group class="mt-3">
              <label for="w-source-uri">
                <font-awesome-icon :icon="['fas', 'link']" class="me-1" />
                AWS CLI Path
                <font-awesome-icon
                  :icon="['fas', 'question-circle']"
                  class="ms-2 text-secondary"
                  id="w-source-uri-help-icon"
                />
              </label>
              <b-tooltip target="w-source-uri-help-icon">
                Source URI
              </b-tooltip>
              <b-form-input
                id="w-source-uri"
                name="wSourceUri"
                v-model="wasabiSourceUri"
                type="text"
                required
              />
            </b-form-group>
            <!-- W Access Key Id -->
            <b-form-group class="mt-3">
              <label for="access-key-id">
                <font-awesome-icon
                  :icon="['fas', 'fingerprint']"
                  class="me-1"
                />
                Access Key ID
              </label>
              <b-form-input
                id="access-key-id"
                name="accessKeyId"
                v-model="wasabiAccessKeyId"
                type="text"
                required
              />
            </b-form-group>
            <!-- W Secret Access Key -->
            <b-form-group class="mt-3">
              <label for="secret-access-key">
                <font-awesome-icon :icon="['fas', 'key']" class="me-1" />
                Secret Access Key
              </label>
              <b-form-input
                id="secret-access-key"
                name="secretAccessKey"
                v-model="wasabiSecretAccessKey"
                type="text"
                required
              />
            </b-form-group>
          </div>

          <!-- WEB upload, with drag and drop -->
          <div v-if="uploadWeb">
            <h5>Select files for upload</h5>
            <div
              class="drag-drop-area mb-3"
              @dragover.prevent="onDragOver"
              @dragleave.prevent="onDragLeave"
              @drop.prevent="onDrop"
              :class="{ 'drag-over': isDragOver }"
            >
              <p>Drag & drop files here</p>
              <p>or</p>
              <b-button variant="outline-primary" @click="browseFiles"
                >Browse Files</b-button
              >
              <input
                type="file"
                ref="fileInput"
                multiple
                @change="handleFileSelect"
                class="d-none"
              />
            </div>

            <!-- List of Selected Files -->
            <div v-if="files.length > 0">
              <h5>Selected Files</h5>
              <ul class="list-group mb-3">
                <li
                  v-for="(file, index) in files"
                  :key="index"
                  class="list-group-item"
                >
                  {{ file.name }} ({{ formatFileSize(file.size) }})
                </li>
              </ul>
            </div>

            <!-- Error: odd number of files -->
            <b-alert
              v-model="uploadWebOddFastqFilesCount"
              variant="danger"
              class="mt-3"
            >
              Upload an even number of FASTQ files. Each pair of files will be
              associated with a sample.
            </b-alert>
          </div>

          <!-- BASESPACE summary -->
          <div v-if="uploadBasespace">
            <h5>Transfer Summary</h5>
            <ul class="non-bulleted list-group mb-3">
              <li class="list-group-item">
                <strong>Samples:</strong>
                {{ bsStore.bsSelection.samples.length }}
              </li>
              <li class="list-group-item">
                <strong>Files:</strong>
                {{ bsStore.bsSelection.files.length }}
              </li>
              <li class="list-group-item">
                <strong>Projects Involved:</strong>
                {{ bsStore.bsSelection.projects.length }}
              </li>
              <li class="list-group-item" v-if="bsStore.bsSelection.totalSize">
                <strong>Total size:</strong>
                {{ formatFileSize(bsStore.bsSelection.totalSize) }}
              </li>
            </ul>
          </div>

          <!-- Code Validation: Storage credits associated with kit(s) or coupon(s) -->
          <div>
            <h5 class="mt-4">Kit validation</h5>
            <p>
              <small>
                Kits associate your samples with the correct barcodes and make
                sure the subsequent processing is accurate. Kits also contain
                storage credits that allow you to upload data to the Sampling
                Human analytics cloud. Do not exchange the correct kit or coupon
                codes if you run out of credits, contact support for assistance
                instead. This will ensure valid code association and prevent any
                disruptions in your workflow or data processing.
              </small>
            </p>
            <CodeValidation
              class="mt-3"
              ref="codeValidation"
              label-text="Kit
          or coupon code"
              button-text="Apply"
              :defaultCodes="[]"
              :requireKit="false"
              :requireExplicitCounts="uploadFtp || uploadWebBcl || uploadWasabi"
              :requiredCouponTypeName="COUPON_TYPE_NAMES.STORAGE"
              :requriedCouponUnitsName="COUPON_UNITS.SAMPLES"
              :requiredCouponUnitsCount="minRequiredStorageCouponUnits"
              :requiredLicenseTypeName="null"
              :immediateValidation="true"
              :hideComponentMessage="true"
            />
          </div>

          <template v-if="codeValidation">
            <!-- ERROR: Code validation -->
            <b-alert
              v-if="codeValidation.message"
              v-model="codeValidation.failure"
              variant="danger"
              class="mt-3"
            >
              {{ codeValidation.message }}
            </b-alert>

            <!-- DISCLAIMER -->
            <b-alert
              v-model="codeValidation.success"
              variant="success"
              class="mt-3"
            >
              <p>
                Double-check the kit/coupon code before proceeding. Incorrect
                code association may lead to down-stream sample annotation and
                analysis errors. Correct kit selection provides details on
                barcode usage and other important information which is critical
                for achieving accurate results.
              </p>
              <p>
                The upload action will consume storage credits associated with
                the kit/coupon code. This action is irreversible.
              </p>
              <p>
                <strong
                  >The following action will consume
                  {{ declaredStorageCouponUnits }} storage credit(s). Beware
                  that if more samples are uploaded, you may be charged for
                  additional storage.</strong
                >
              </p>
            </b-alert>
          </template>

          <!-- Optional Auto-Features -->
          <div>
            <h5 class="mt-4">Auto-Features</h5>
            <b-alert
              v-model="autoFeaturesDisabled"
              variant="secondary"
              class="mt-3"
              show
            >
              <template v-if="uploadWeb">
                Auto-features are disabled for all web uploads. Please annotate
                your samples in the data manager when uploads are finished.
              </template>
              <template v-else>
                Auto-features are disabled when multipe kit/coupon codes are
                provided. Please annotate you samples manually later in the data
                manager.
              </template>
            </b-alert>
            <b-form-checkbox
              v-if="!uploadBasespace"
              id="auto-sample-annotation"
              v-model="autoSampleAnnotation"
              :disabled="autoFeaturesDisabled"
              name="auto-sample-annotation"
              switch
              class="mt-3"
            >
              Auto Sample Annotation
              <font-awesome-icon
                :icon="['fas', 'question-circle']"
                class="ms-2 text-secondary"
                id="icon-tooltip-auto-sample-annotation"
              />
            </b-form-checkbox>
            <b-form-checkbox
              id="auto-project-definition"
              v-model="autoProjectDefinition"
              :disabled="autoFeaturesDisabled"
              name="auto-project-definition"
              switch
              class="mt-3"
            >
              Auto Project Definition
              <font-awesome-icon
                :icon="['fas', 'question-circle']"
                class="ms-2 text-secondary"
                id="icon-tooltip-auto-project-definition"
              />
            </b-form-checkbox>
            <b-form-checkbox
              id="auto-sample-reconstruction"
              v-model="autoSampleReconstruction"
              :disabled="autoFeaturesDisabled"
              name="auto-sample-reconstruction"
              switch
              class="mt-3"
            >
              Auto Sample Reconstruction
              <font-awesome-icon
                :icon="['fas', 'question-circle']"
                class="ms-2 text-secondary"
                id="icon-tooltip-auto-sample-reconstruction"
              />
            </b-form-checkbox>
            <BTooltip target="icon-tooltip-auto-sample-annotation">
              Automatically annotate samples when upload is finished.
            </BTooltip>
            <BTooltip target="icon-tooltip-auto-project-definition">
              Automatically create project with annotated samples.
            </BTooltip>
            <BTooltip target="icon-tooltip-auto-sample-reconstruction">
              Automatically start the sample reconstruction process when project
              is defined.
            </BTooltip>

            <BCollapse v-model="autoSampleReconstruction" class="mt-3">
              <!-- NextFlow Workflow: Pipeline Selection -->
              <h6 class="mt-4 mb-1">
                <font-awesome-icon :icon="['fas', 'server']" class="me-2" />
                Pipeline selection
                <font-awesome-icon
                  :icon="['fas', 'question-circle']"
                  class="ms-2 text-secondary"
                  id="pipeline-selection-help-icon"
                />
                <BTooltip target="pipeline-selection-help-icon">
                  <div style="text-align: justify">
                    <p>
                      Automatic sample reconstruction will attempt to
                      automatically reconstruct your samples based using the
                      selected pipeline.
                    </p>
                  </div>
                </BTooltip>
              </h6>

              <PipelinePicker
                class="mt-3"
                v-model="selectedPipeline"
                :enabled="!nfCustomWorkflow"
                :label="null"
                :type="null"
                :premium="null"
                :activeOnly="true"
                :allowNullPipeline="false"
              />

              <!-- Custom Nextflow Workflow-->
              <template v-if="userStore.developer">
                <!-- UMI Length -->
                <label class="mt-3">
                  Custom Workflow (developer option)
                  <font-awesome-icon
                    :icon="['fas', 'question-circle']"
                    id="nf-workflow-location-help"
                    class="ms-2 text-secondary"
                  />
                  <BTooltip
                    target="nf-workflow-location-help"
                    class="large-tooltip"
                  >
                    <div style="text-align: justify">
                      <p>
                        Specify the NextFlow workflow URL. Accepted options are:
                      </p>
                      <ul>
                        <li>
                          <code>
                            https://gitlab.samplinghuman.com/group/project/-/tree/ref/workflow-path
                          </code>
                        </li>
                        <li>
                          <code>
                            https://gitlab.samplinghuman.com/group/project/-/blob/ref/workflow-path/main.nf
                          </code>
                        </li>
                        <li>
                          <code>
                            gitlab://group/project/-/raw/ref/workflow-path/main.nf
                          </code>
                        </li>
                        <li><code>gs://bucket/workflow-path/main.nf</code></li>
                      </ul>
                      <p>
                        Best default for humans is to use the GitLab browser URL
                        from the repository page. The backend canonicalizes that
                        to the raw workflow source internally. Example browser
                        URL:
                        <code>
                          https://gitlab.samplinghuman.com/samplinghuman/ax-stack/nextflow/pipelines/rnaseq-data-pipeline/-/tree/v0.1.0-dev
                        </code>
                        is automatically converted to canonical form:
                        <code>
                          gitlab://samplinghuman/ax-stack/nextflow/pipelines/rnaseq-data-pipeline/-/raw/v0.1.0-dev/main.nf
                        </code>
                        by the backend.
                      </p>
                    </div>
                  </BTooltip>
                </label>
                <BFormGroup>
                  <BInputGroup>
                    <!-- Prepend override checkbox -->
                    <template #prepend>
                      <BButton variant="outline-secondary">
                        <BFormCheckbox
                          v-model="nfCustomWorkflow"
                          name="nf-custom-workflow"
                        ></BFormCheckbox>
                      </BButton>
                    </template>
                    <!-- Custom Workflow Location -->
                    <BFormInput
                      v-model="nfWorkflowLocation"
                      id="nf-workflow-location"
                      name="nf-workflow-location"
                      placeholder="Enter workflow location"
                      :disabled="!nfCustomWorkflow"
                      @change="nfCustomWorkflow = true"
                    ></BFormInput>
                  </BInputGroup>
                </BFormGroup>
              </template>
              <!-- end of Custom NextFlow workflow options -->
            </BCollapse>
          </div>

          <!-- Developer options -->
          <DeveloperOptions
            v-if="userStore.developer"
            ref="devOptions"
            size="sm"
            class="mt-3"
          />

          <!-- Start Upload Button -->
          <b-button
            class="mt-3"
            variant="primary"
            type="submit"
            :disabled="
              codeValidation?.failure ||
              (files.length === 0 && uploadWeb) ||
              uploadWebOddFastqFilesCount ||
              (linesFtp.length === 0 && uploadFtp) ||
              mutationInProgress
            "
          >
            <!-- Start WEB upload -->
            <template v-if="uploadWeb">
              <span v-if="mutationInProgress">
                Starting Upload
                <b-spinner small />
              </span>
              <span v-else-if="mutationShowSuccess">
                Upload Started
                <font-awesome-icon
                  :icon="['fa', 'check-double']"
                  class="me-2"
                />
              </span>
              <span v-else>Start Upload</span>
            </template>
            <!-- Start FTP or BASESPACE transfer -->
            <template v-else>
              <span v-if="mutationInProgress">
                Starting Transfer
                <b-spinner small />
              </span>
              <span v-else-if="mutationShowSuccess">
                Transfer Started
                <font-awesome-icon
                  :icon="['fa', 'check-double']"
                  class="me-2"
                />
              </span>
              <span v-else>Start Transfer</span>
            </template>
          </b-button>

          <!-- ERROR: Mutation -->
          <b-alert
            v-model="mutationError"
            variant="danger"
            class="mt-3"
            dismissible
          >
            <strong>
              <span v-if="uploadWeb">Upload failed:</span>
              <span v-else>Transfer failed:</span>
            </strong>
            {{ mutationError }}
          </b-alert>
        </BForm>
      </div>
    </b-card>
  </div>
</template>

<style scoped>
.drag-drop-area {
  border: 1px dashed #6c757d;
  border-radius: 5px;
  padding: 30px;
  text-align: center;
  cursor: pointer;
  position: relative;
}

.drag-drop-area.drag-over {
  background-color: #e9ecef;
}

.drag-drop-area p {
  margin: 0;
}
</style>
